import React, { useState } from 'react';
import { Pressable } from 'react-native';

import { BrandColor } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';

import {
  BodyText,
  HeaderText,
  Stack,
} from '../../../ui';

import { ProviderService } from '../../../../api/Providers/Services';
import Currency from '../../../../modules/Currency';

import BookButton from '../BookButton';
import { ProviderServicePromotion } from '../../../../api/Providers/Promotions';

const MAX_DESCRIPTION_LENGTH = 100;

const serviceDurationFormatted = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const min = minutes % 60;
  return hours > 0 ? `${hours} hr ${min} min` : `${minutes} min`;
};

const truncate = (text: string) => (text.length > MAX_DESCRIPTION_LENGTH
  ? `${text.substring(0, MAX_DESCRIPTION_LENGTH)}...`
  : text
);

export type ProviderServiceRevamp = ProviderService & {
  serviceRank?: number;
  numRatings?: number;
  averageStars?: string;
  imageUrls?: string[];
  numTotalImages?: number;
  isHighlightedService?: boolean;
};

export type PromotedServiceItemProps = {
  service: ProviderServiceRevamp;
  providerId: number;
  providerPhone: string;
  isOnlineBookable: boolean;
  showAndUp?: boolean;
  promotion: ProviderServicePromotion;
};

export const PromotedServiceItem: React.FC<PromotedServiceItemProps> = ({
  service,
  providerId,
  providerPhone,
  isOnlineBookable,
  showAndUp = false,
  promotion,
}) => {
  const [
    showFullDescription,
    setShowFullDescription,
  ] = useState(promotion.description?.length < MAX_DESCRIPTION_LENGTH);
  const serviceDuration = serviceDurationFormatted(
    service.total_duration_minutes || service.duration_minutes,
  );

  let promotionHeader = '';
  if (promotion.discount_type === 'flat-rate') {
    promotionHeader = `$${promotion.discount_quantity} off`;
  } else if (promotion.discount_type === 'percentage') {
    promotionHeader = `${promotion.discount_quantity}% off`;
  }

  return (
    <Stack
      paddingY={24}
      space={16}
      borderStyle="solid"
      borderBottomWidth={1}
      borderBottomColor={BrandColor.LightGrey}
      testID="profile-service-item-revamp-card"
    >
      <Box
        borderRadius={100}
        color={BrandColor.Red}
        paddingX={8}
        paddingY={0}
        alignSelf="baseline"
        maxWidth="100%"
        testID="profile-service-item-revamp-promotion-badge"
      >
        <Inline alignItems="center" justifyContent="center">
          <Box paddingTop={4}>
            <HeaderText
              color={BrandColor.SuperLight}
              level={3}
              testID="profile-promotion-item-value"
            >
              {promotionHeader}
            </HeaderText>
          </Box>
          <Box paddingBottom={1} paddingLeft={8} flex={1}>
            <BodyText
              color={BrandColor.SuperLight}
              appearance="normal"
              whiteSpace="nowrap"
              testID="profile-promotion-item-name"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {`${promotion.name}`}
            </BodyText>
          </Box>
        </Inline>
      </Box>
      <Inline>
        <Stack space={8} flexShrink={1}>
          <HeaderText level={4} testID="profile-service-item-revamp-service-name">
            {service.name}
          </HeaderText>
          <Inline space={8} alignItems="center">
            {service.isHighlightedService && (
              <Box
                paddingX={8}
                paddingY={4}
                borderRadius={100}
                color={BrandColor.LightBlue}
                testID="profile-service-item-revamp-highlighted-service"
              >
                <BodyText appearance="small" color={BrandColor.Metal}>
                  Highlighted
                </BodyText>
              </Box>
            )}
            {service.serviceRank && (
              <Box
                paddingX={8}
                paddingY={4}
                borderRadius={100}
                color={BrandColor.FloraLight}
                testID="profile-service-item-revamp-top-service-rank"
              >
                <BodyText appearance="small" color={BrandColor.Metal}>
                  {`#${service.serviceRank} booked`}
                </BodyText>
              </Box>
            )}
            <Inline space={8} alignItems="center">
              <BodyText appearance="small" color={BrandColor.Metal} testID="profile-service-item-revamp-cost">
                {/* eslint-disable-next-line max-len */}
                {Currency.shortFormat(service.cost)} {showAndUp && <BodyText color={BrandColor.Ash}>+</BodyText>}
              </BodyText>
              <BodyText appearance="small" color={BrandColor.Silver}>&#183;</BodyText>
              <BodyText appearance="small" color={BrandColor.Ash} testID="profile-service-item-revamp-duration">
                {serviceDuration}
              </BodyText>
            </Inline>
          </Inline>
        </Stack>
        <Inline
          flexGrow={1}
          justifyContent="flex-end"
          alignItems="center"
          paddingLeft={8}
        >
          <BookButton
            bookText="See Times"
            style={{
              display: 'flex',
              height: 32,
              textWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            size="medium"
            isOnlineBookable={isOnlineBookable}
            service={service}
            providerId={providerId}
            providerPhone={providerPhone}
            promotion={promotion}
            testID="profile-service-item-revamp-book-button"
          />
        </Inline>
      </Inline>
      {!!promotion.description && (
        <Pressable
          onPress={() => setShowFullDescription(!showFullDescription)}
          testID="profile-service-item-revamp-description"
        >
          <BodyText color={BrandColor.Ash}>
            {showFullDescription ? promotion.description : truncate(promotion.description)}
          </BodyText>
        </Pressable>
      )}
      {promotion.book_start_time && promotion.book_end_time && (
        <Inline space={8} alignItems="center">
          <BodyText appearance="medium" color={BrandColor.Ash} testID="profile-service-item-revamp-promotion-duration">
            {`${promotion.book_start_time.format('MM/DD/YYYY')} - ${promotion.book_end_time.format('MM/DD/YYYY')}`}
          </BodyText>
        </Inline>
      )}
    </Stack>
  );
};
